import React, { FC, useContext, useMemo } from "react";
import { DiscountedProduct } from "./DiscountedProductsTable.tsx";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { OptionGroupProducts } from "./OptionGroupProducts.tsx";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper.tsx";
import {
  OptionGroupAttributes,
  useLazyFetchOptionGroupsQuery,
} from "../../../../Domains/OptionGroups/api/optionGroupsApi.ts";
import _ from "lodash";
import { AutocompleteOptionGroup } from "@jamezz/react-components";
import { RenderOption } from "../ProductOptions/AutocompoleteOptionGroupRenderOption.tsx";
import { ExistingResourceObject } from "../../../../Domains/Products/api/baseTypes.ts";
import { ProductOptionGroupDataContext } from "./ProductAndOptionGroupContext.tsx";

type ProductOptionGroupsProps = {
  salesAreaId: number;
  discountedProduct: DiscountedProduct;
  onChange: (product: DiscountedProduct) => void;
  expanded?: boolean;
};
export const ProductOptionGroups: FC<ProductOptionGroupsProps> = ({
  salesAreaId,
  discountedProduct,
  onChange,
  expanded = false,
}) => {
  const { setOptionGroupData, optionGroupData, productData } = useContext(ProductOptionGroupDataContext);

  const value = useMemo(() => {
    return discountedProduct.optionGroups?.map((optionGroup) => String(optionGroup.id)) ?? [];
  }, [discountedProduct]);
  return (
    <Accordion defaultExpanded={expanded} sx={{ borderTop: 0 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component={"span"}>
          {productData[discountedProduct.id]?.attributes?.naam ?? discountedProduct.id}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <TextField
            type={"number"}
            value={discountedProduct.maxCount}
            label={"Max count"}
            onChange={(e) => {
              onChange({ ...discountedProduct, maxCount: parseInt(e.target.value) });
            }}
          />
          <AutocompleteOptionGroup<true, OptionGroupAttributes, true>
            multiple
            menuSalesareaId={salesAreaId}
            fetchQuery={useLazyFetchOptionGroupsQuery}
            defaultFilters={{
              vestiging_id: { field: "vestiging_id", operator: "=", value: salesAreaId },
              product_id: { field: "product_id", operator: "=", value: discountedProduct.id },
            }}
            value={value}
            inputFieldProps={{ sx: { minWidth: "210px" } }}
            onFetched={(newData) => {
              setOptionGroupData(_.merge({}, optionGroupData, _.keyBy(newData, "id")));
            }}
            onChange={(option: ExistingResourceObject<OptionGroupAttributes>[]) => {
              onChange({
                ...discountedProduct,
                optionGroups: _.map(option, (option) => ({
                  id: parseInt(option.id),
                  maxCount: 1,
                  allOptionsCount: true,
                  options: [],
                })),
              });
            }}
            renderOption={(props, option) => {
              return <RenderOption option={option} />;
            }}
          />
        </Stack>
        {(discountedProduct.optionGroups ?? []).length > 0 ? (
          <>
            <Typography variant={"body2"} sx={{ mt: 2 }}>
              <TranslationWrapper tKey={"menu.items.optionGroups"} tDefault={"Option groups"} />
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Max Count</TableCell>
                    <TableCell>Applies to all options</TableCell>
                    <TableCell />
                    <TableCell sx={{ maxWidth: "40px" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {discountedProduct.optionGroups?.map((optionGroup) => {
                    return (
                      <OptionGroupProducts
                        key={optionGroup.id}
                        salesAreaId={salesAreaId}
                        optionGroup={optionGroup}
                        onChange={(changedOptionGroup) => {
                          onChange({
                            ...discountedProduct,
                            optionGroups: discountedProduct.optionGroups?.map((optionGroup) => {
                              if (optionGroup.id === changedOptionGroup.id) {
                                return changedOptionGroup;
                              }

                              return optionGroup;
                            }),
                          });
                        }}
                        onRemove={(removedOptionGroup) => {
                          onChange({
                            ...discountedProduct,
                            optionGroups: discountedProduct.optionGroups?.filter(
                              (optionGroup) => optionGroup.id !== removedOptionGroup.id
                            ),
                          });
                        }}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};
